import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import usePermission from "utils/usePermission";
import styles from "./../MainHeader.module.scss";
import SlidingWidget, { CaptionButton, SlidingWidgetFooter } from "./SlidingWidget"
import { SearchWidget, TreeList, InlineLoader, InnerTab, useTable } from "@netapp/bxp-design-system-react";
import classNames from "classnames";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { allMenus as i18nNamespace } from "../../../../i18n/namespace";

const MEMBER_ORGS = 'member_orgs';
const PARTNER_ORGS = 'partner_orgs';
const ENABLED = 'ENABLED';

const OrganizationMenuFooter = ({ activeOrg, selectedOrg }) => {
    const dispatch = useDispatch();

    return <SlidingWidgetFooter onSwitch={() => {
        if (activeOrg === selectedOrg) {
            return;
        }

        dispatch({
            type: "TENANCYV4:SET-ORG",
            payload: {
                orgId: selectedOrg.id
            }
        });
    }} onClose={() => {
        dispatch({
            type: "MENU:CLOSE-WIDGET"
        })
    }} isSwitchDisabled={activeOrg.id === selectedOrg.id}/>
};

const MenuContent = React.memo(({ setSelectedOrg }) => {
    const activeOrg = useSelector(state => state.tenancy.selectedOrg);
    const orgs = useSelector(state => state.tenancy.orgs) || [];
    const partnerOrgs = useSelector(state => state.tenancy.partnerOrgs);
    const featuresByKey = useSelector(state => state.features.byKey);
    const darkSite = process.env.REACT_APP_IS_DARK === 'true';
    const isSAAS = process.env.REACT_APP_IS_SAAS === 'true';
    const isPartnershipsEnabled = isSAAS && !darkSite && featuresByKey["NetApp.TenancyV4/partnerships"].active;
    const [currentTab, setCurrentTab] = useState(() => !isPartnershipsEnabled || !activeOrg?.isPartnerOrg ? MEMBER_ORGS : PARTNER_ORGS);
    const [clearSearchInput, setClearSearchInput] = useState(false);
    const { t } = useTranslation(i18nNamespace);

    // const permissions = useMemo(() => ({ manage: hasPermission({ permissionType: "switch", permissionKey: "organization" })}), [hasPermission]);

    const getActiveOrgs = () => (currentTab === MEMBER_ORGS ? orgs : (partnerOrgs || []));
    
    // filter columns can be extended in future if needed
    const columns = ["name"];

    const { organizedRows: filteredOrgs, updateTextFilter } = useTable({
        columns,
        rows: getActiveOrgs() || [],
    });

    const onChangeEvent = (org) => {
        setSelectedOrg(org[0]);
    }

    const customStyles = {
        treeItemBase: {
            height: '55px'
        },
        treeListBase: {
            maxHeight: 'unset'
        }
    }

    // simplifies tab switch/reset (just pass new rows to useTable and clear text filter).
    const onTabChange = (tab) => {
        // Clear search input on tab change
        setClearSearchInput(prevState => !prevState)
        setCurrentTab(tab);
    };

    // In local UI disable all the organization other than selected organization.
    const disableElement = (ele) => {
        if (ele.id !== activeOrg.id && process.env.REACT_APP_IS_SAAS !== "true") {
            return true
        }
        if (currentTab === PARTNER_ORGS && ele.state !== ENABLED) {
            return true;
        }
    }

    return <div style={{ paddingTop: 20 }} className={classNames(styles['org-menu-content'])}>
        {isPartnershipsEnabled && partnerOrgs.length > 0 && <InnerTab>
            <InnerTab.Button isActive={currentTab === MEMBER_ORGS} onClick={() => onTabChange(MEMBER_ORGS)}>{t('enterprise_orgs')}</InnerTab.Button>
            <InnerTab.Button isActive={currentTab === PARTNER_ORGS} onClick={() => onTabChange(PARTNER_ORGS)}>{t('partner_orgs')}</InnerTab.Button>
        </InnerTab>}
        <SearchWidget placeholder={t("search_organization")} alwaysOpen className={styles['org-search-widget']} setFilter={updateTextFilter} clearValue={clearSearchInput} />
        <div style={{ margin: "0px 40px" }}>
            <TreeList
                treeData={filteredOrgs}
                menuType={'singleRadio'}
                uniqueIdentifier={'id'}
                labelKey={'name'}
                bottomBorder={true}
                selectedValues={[activeOrg]}
                onChange={onChangeEvent}
                customStyles={customStyles}
                isOptionDisabled={disableElement} />
        </div>
    </div>
});

export default () => {
    const activeOrg = useSelector(state => state.tenancy.selectedOrg);
    const [selectedOrg, setSelectedOrg] = useState(activeOrg);
    const { t } = useTranslation(i18nNamespace);
    const orgName = activeOrg?.name || t("not_applicable");
    const orgsFullyLoaded = useSelector(state => state.tenancy.orgsFullyLoaded);
    const userId = useSelector(state => state.auth.userId);
    const dispatch = useDispatch();

    const toggleHandler = (isActive) => {
        if (!isActive && !orgsFullyLoaded) {
            dispatch({
                type: "TENANCYV4:FETCH-ORGS",
                payload: { userId, pollAgents: true, avoidLoadScopes: true }
            })
        }
    }

    return <SlidingWidget
        widgetKey="organization"
        menuTitle={t("organization")}
        canClose={() => process.env.REACT_APP_IS_SAAS !== "true"}
        onToggle={toggleHandler}
        triggerClassName={styles['caption-button']}
        triggerActiveClassName={[styles['caption-button-active']]}
        TriggerButton={(props) => <CaptionButton title={t("organization")} value={orgName} {...props} />}
        MenuFooter={process.env.REACT_APP_IS_SAAS !== "true" ? undefined : () => <OrganizationMenuFooter activeOrg={activeOrg} selectedOrg={selectedOrg} />}
    >
        {orgsFullyLoaded ? <MenuContent setSelectedOrg={setSelectedOrg} selectedOrg={selectedOrg} /> : <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><InlineLoader /></div>}
    </SlidingWidget>
};